import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

// Firebase
import { collection, query, onSnapshot, updateDoc, doc, where } from "firebase/firestore";
import { db } from "firebaseDb"; // <-- adjust import to your firebase config

// @material-ui core
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import Button from "@mui/material/Button";
import MenuItem from "@mui/material/MenuItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Typography from "@mui/material/Typography";

// MD2 components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import Breadcrumbs from "examples/Breadcrumbs";

// styles
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";

// context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

import SignOut from "../../../layouts/authentication/sign-out";

const SETUP_ENTITY_SEGMENTS = ["chem", "employee", "sprayer", "orchard"];

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [notes, setNotes] = useState([]);
  const location = useLocation();
  const route = location.pathname.split("/").filter(Boolean);
  const isSetupEntityPath = route[0] === "setup" && SETUP_ENTITY_SEGMENTS.includes(route[1]);
  const breadcrumbRoute = isSetupEntityPath ? ["setup", route[route.length - 1]] : route;
  const navigate = useNavigate();

  useEffect(() => {
    if (fixedNavbar) setNavbarType("sticky");
    else setNavbarType("static");

    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }
    window.addEventListener("scroll", handleTransparentNavbar);
    handleTransparentNavbar();
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  // 🔥 Firestore real-time listener for unread notes
  useEffect(() => {
    const q = query(collection(db, "Notes"), where("read", "==", false));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedNotes = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setNotes(fetchedNotes);
    });
    return () => unsubscribe();
  }, []);

  const unreadNotes = notes.filter((n) => n.read === false);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () => setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);

  const markAsRead = async (noteId) => {
    await updateDoc(doc(db, "Notes", noteId), { read: true });
  };

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      {notes.length === 0 && (
        <MenuItem>
          <ListItemIcon>
            <Icon>done_all</Icon>
          </ListItemIcon>
          <ListItemText primary="No notifications" />
        </MenuItem>
      )}

      {notes.map((note) => (
        <MenuItem
          key={note.id}
          onClick={() => {
            navigate(`/sprays/${note.sprayID}`);
            handleCloseMenu();
          }}
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
        >
          <ListItemIcon>
            <Icon>{note.read ? "drafts" : "markunread"}</Icon>
          </ListItemIcon>

          <ListItemText
            primary={
              <Typography variant="body2" noWrap>
                {note.message || "Notification"}
              </Typography>
            }
            secondary={
              note.siteName ? (
                <Typography variant="caption" color="text.secondary" noWrap>
                  {note.siteName}
                </Typography>
              ) : null
            }
          />

          {!note.read && (
            <Button
              size="large"
              onClick={(e) => {
                e.stopPropagation(); // prevent navigate on button click
                markAsRead(note.id);
              }}
            >
              <Icon>delete</Icon>
            </Button>
          )}
        </MenuItem>
      ))}
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;
      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }
      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <Breadcrumbs
            icon="home"
            title={breadcrumbRoute[breadcrumbRoute.length - 1]}
            route={breadcrumbRoute}
            light={light}
          />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox pr={1}>
              <MDInput label="Search here" />
            </MDBox>
            <MDBox color={light ? "white" : "inherit"}>
              <SignOut />
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <Badge badgeContent={unreadNotes.length} color="error">
                  <Icon sx={iconsStyle}>notifications</Icon>
                </Badge>
              </IconButton>
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
